import { formatOrgnr } from '@autho/i18n';
import type { ReactNode } from 'react';
import { cn } from './cn.ts';

// Eight hues chosen to stay distinguishable and meet contrast as a small swatch next to text.
const SWATCHES = [
  'bg-entity-1',
  'bg-entity-2',
  'bg-entity-3',
  'bg-entity-4',
  'bg-entity-5',
  'bg-entity-6',
  'bg-entity-7',
  'bg-entity-8',
] as const;

/** Stable per entity: the same id always gets the same colour. */
export function entitySwatch(id: string): (typeof SWATCHES)[number] {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return SWATCHES[hash % SWATCHES.length] ?? SWATCHES[0];
}

/** Name + org number, colour-stable per entity (Design Principles: EntityChip). */
export function EntityChip({
  id,
  name,
  orgnr,
  showOrgnr = true,
  dimmed = false,
  after,
}: {
  id: string;
  name: string;
  orgnr: string;
  showOrgnr?: boolean;
  dimmed?: boolean;
  after?: ReactNode;
}) {
  return (
    // Dimmed by the swatch alone: the name keeps its row's colour, so a company out of view stays readable at
    // AA contrast (an opacity on the whole chip took `text-muted` below 4.5:1, found by axe in R6's walk).
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <span
        aria-hidden
        className={cn('size-2 shrink-0 rounded-sm', entitySwatch(id), dimmed && 'opacity-40')}
      />
      <span className="truncate">{name}</span>
      {showOrgnr && (
        <span className="shrink-0 text-xs tabular-nums text-muted">{formatOrgnr(orgnr)}</span>
      )}
      {after}
    </span>
  );
}
